import { notification } from 'antd';
import { getZohoConfig } from 'library/utils/zohoConfig';

declare global {
  interface Window {
    ZPayments?: any;
  }
}

export interface ZohoPaymentsSession {
  payments_session_id: string;
  amount?: string;
  currency?: string;
  description?: string;
  reference_number?: string;
}

/**
 * Why the payment popup cannot open, or null when it can. The account ID and API key are baked into the build from
 * packages/hotel/.env; a build made without them makes Zoho answer "Required params to load the widget are not found!".
 */
export const zohoNotReadyReason = (): 'not_configured' | 'not_loaded' | null => {
  const { account_id, api_key } = getZohoConfig();
  if (!account_id || !api_key) return 'not_configured';
  if (typeof window === 'undefined' || !window.ZPayments) return 'not_loaded';
  return null;
};

/**
 * Call before creating a purchase: tells the host plainly, and creates nothing, when the popup could not open anyway.
 * Returns true when it is fine to go on.
 */
export const ensureZohoReady = (): boolean => {
  const reason = zohoNotReadyReason();
  if (reason === 'not_configured') {
    // The setting names are left out of this text on purpose: scripts/check-prod-build.sh looks for them in the finished bundle.
    // eslint-disable-next-line no-console
    console.error('Zoho Payments is not set up in this build: the account id or the API key is empty.');
    notification.error({
      message: 'Payments are not available right now',
      description: 'We could not start the payment. Please try again later or contact support.',
    });
    return false;
  }
  if (reason === 'not_loaded') {
    notification.error({
      message: 'Payment system error',
      description: 'Payment system is not loaded. Please refresh the page and try again.',
    });
    return false;
  }
  return true;
};

/** Opens the Zoho Payments popup for a payment session. Resolves true when the host paid (the bank may still confirm it later). */
export const openZohoPayment = async (session: ZohoPaymentsSession, fallbackAmount: string): Promise<boolean> => {
  if (!ensureZohoReady()) return false;
  const { account_id, api_key, domain, is_test_mode } = getZohoConfig();

  const instance = new window.ZPayments({
    account_id,
    domain: domain || 'IN',
    otherOptions: {
      api_key,
      ...(is_test_mode ? { is_test_mode: true } : {}),
    },
  });
  const options = {
    amount: session.amount || fallbackAmount,
    currency_code: session.currency || 'INR',
    payments_session_id: session.payments_session_id,
    currency_symbol: '₹',
    business: 'Unpaqd',
    description: session.description || 'Host Pro plan',
    reference_number: session.reference_number,
  };

  try {
    const paymentData = await instance.requestPaymentMethod(options);
    return !!(paymentData && paymentData.payment_id);
  } catch (err: any) {
    if (err?.code !== 'widget_closed') {
      notification.error({
        message: 'Payment failed',
        description: err?.message || 'Some error occurred. Please try again.',
      });
    }
    return false;
  } finally {
    await instance.close();
  }
};
